import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Receipt as ReceiptIcon,
  Assessment as AssessmentIcon,
  CloudUpload as CloudUploadIcon,
} from "@mui/icons-material";
import { useRouter } from "../../../hooks/useRouter";
import { formatCurrency } from "../../../utils";
import FinanceContainer from "../containers/FinanceContainer";
import ImportDialog from "../components/ImportDialog";
import config from "../../../config";

interface Development {
  id: string;
  name: string;
  description?: string;
}

interface Debtor {
  id: string;
  developmentId?: string;
  developmentName?: string;
  unitRef?: string;
  fullName: string;
  contactPhone?: string;
  email?: string;
  propertyType?: string;
  purchasePrice?: number;
  depositPaid?: number;
  totalInvoiced?: number;
  totalReceipts?: number;
  balance?: number;
  active?: boolean;
}

const DebtorsList: React.FC = () => {
  const router = useRouter();
  const [debtors, setDebtors] = useState<Debtor[]>([]);
  const [developments, setDevelopments] = useState<Development[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedDebtor, setSelectedDebtor] = useState<Debtor | null>(null);
  const [sortField, setSortField] = useState<keyof Debtor>("fullName");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  // Form state for new/edit debtor
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    secondaryEmail: "",
    tertiaryEmail: "",
    contactPhone: "",
    developmentId: "",
    unitRef: "",
    propertyType: "",
    purchasePrice: 0,
    depositPaid: 0,
    interestRate: 0,
    salesRep: "",
    notes: "",
  });

  useEffect(() => {
    fetchDebtors();
    fetchDevelopments();
  }, []);

  const fetchDevelopments = async () => {
    try {
      const response = await fetch(`${config.API_BASE_URL}/developments`);
      if (response.ok) {
        const data = await response.json();
        setDevelopments(data);
      }
    } catch (error) {
      console.error("Error fetching developments:", error);
    }
  };

  const fetchDebtors = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("vms_access_token");
      const response = await fetch(`${config.API_BASE_URL}/debtors`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setDebtors(data);
      }
    } catch (error) {
      console.error("Error fetching debtors:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: keyof Debtor) => {
    const isAsc = sortField === field && sortDirection === "asc";
    setSortDirection(isAsc ? "desc" : "asc");
    setSortField(field);
  };

  const filteredDebtors = debtors
    .filter(
      (debtor) =>
        debtor.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        debtor.unitRef?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        debtor.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        debtor.developmentName
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()),
    )
    .sort((a, b) => {
      const aVal = a[sortField] ?? "";
      const bVal = b[sortField] ?? "";
      if (sortDirection === "asc") {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });

  const handleOpenDialog = (debtor?: Debtor) => {
    if (debtor) {
      setSelectedDebtor(debtor);
      setFormData({
        fullName: debtor.fullName || "",
        email: debtor.email || "",
        secondaryEmail: (debtor as any).secondaryEmail || "",
        tertiaryEmail: (debtor as any).tertiaryEmail || "",
        contactPhone: debtor.contactPhone || "",
        developmentId: debtor.developmentId || "",
        unitRef: debtor.unitRef || "",
        propertyType: debtor.propertyType || "",
        purchasePrice: debtor.purchasePrice || 0,
        depositPaid: debtor.depositPaid || 0,
        interestRate: 0,
        salesRep: "",
        notes: "",
      });
    } else {
      setSelectedDebtor(null);
      setFormData({
        fullName: "",
        email: "",
        secondaryEmail: "",
        tertiaryEmail: "",
        contactPhone: "",
        developmentId: "",
        unitRef: "",
        propertyType: "",
        purchasePrice: 0,
        depositPaid: 0,
        interestRate: 0,
        salesRep: "",
        notes: "",
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedDebtor(null);
  };

  const handleSaveDebtor = async () => {
    try {
      const token = localStorage.getItem("vms_access_token");
      const url = selectedDebtor
        ? `${config.API_BASE_URL}/debtors/${selectedDebtor.id}`
        : `${config.API_BASE_URL}/debtors`;
      const method = selectedDebtor ? "PATCH" : "POST";

      // Find development name
      const selectedDev = developments.find(
        (d) => d.id === formData.developmentId,
      );
      const payload = {
        ...formData,
        developmentName: selectedDev?.name || "",
      };

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        fetchDebtors();
        handleCloseDialog();
      }
    } catch (error) {
      console.error("Error saving debtor:", error);
    }
  };

  const handleDeleteDebtor = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this debtor?")) return;

    try {
      const token = localStorage.getItem("vms_access_token");
      const response = await fetch(`${config.API_BASE_URL}/debtors/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        fetchDebtors();
      }
    } catch (error) {
      console.error("Error deleting debtor:", error);
    }
  };

  const getBalanceColor = (balance: number | undefined) => {
    if (!balance || balance === 0) return "success";
    if (balance > 0) return "error";
    return "default";
  };

  const summaryCards = [
    {
      title: "Total Debtors",
      value: debtors.length,
      color: "#1976d2",
    },
    {
      title: "Total Outstanding",
      value: formatCurrency(
        debtors.reduce((sum, d) => sum + (d.balance || 0), 0),
      ),
      color: "#d32f2f",
    },
    {
      title: "Total Invoiced",
      value: formatCurrency(
        debtors.reduce((sum, d) => sum + (d.totalInvoiced || 0), 0),
      ),
      color: "#388e3c",
    },
    {
      title: "Total Receipts",
      value: formatCurrency(
        debtors.reduce((sum, d) => sum + (d.totalReceipts || 0), 0),
      ),
      color: "#7b1fa2",
    },
  ];

  return (
    <FinanceContainer>
      <Box sx={{ p: 2 }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{ display: "flex", alignItems: "center", gap: 2 }}
        >
          <AssessmentIcon /> Debtors Management
        </Typography>

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {summaryCards.map((card, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card sx={{ backgroundColor: card.color, color: "white" }}>
                <CardContent>
                  <Typography variant="subtitle2">{card.title}</Typography>
                  <Typography variant="h5">{card.value}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Actions Bar */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 2,
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <TextField
            placeholder="Search debtors..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 300 }}
          />
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              onClick={() => setImportDialogOpen(true)}
            >
              Import
            </Button>
            <Button
              variant="outlined"
              startIcon={<AssessmentIcon />}
              onClick={() => router.push("/finance/debtors/age-analysis")}
            >
              Age Analysis
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add Debtor
            </Button>
          </Box>
        </Box>

        {/* Debtors Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell>
                  <TableSortLabel
                    active={sortField === "fullName"}
                    direction={sortField === "fullName" ? sortDirection : "asc"}
                    onClick={() => handleSort("fullName")}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>Unit Ref</TableCell>
                <TableCell>Development</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell align="right">Total Invoiced</TableCell>
                <TableCell align="right">Total Receipts</TableCell>
                <TableCell align="right">Balance</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredDebtors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No debtors found
                  </TableCell>
                </TableRow>
              ) : (
                filteredDebtors.map((debtor) => (
                  <TableRow key={debtor.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {debtor.fullName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {debtor.email}
                      </Typography>
                    </TableCell>
                    <TableCell>{debtor.unitRef || "-"}</TableCell>
                    <TableCell>{debtor.developmentName || "-"}</TableCell>
                    <TableCell>{debtor.contactPhone || "-"}</TableCell>
                    <TableCell align="right">
                      {formatCurrency(debtor.totalInvoiced || 0)}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(debtor.totalReceipts || 0)}
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={formatCurrency(debtor.balance || 0)}
                        color={getBalanceColor(debtor.balance)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() =>
                          router.push(`/finance/debtors/${debtor.id}`)
                        }
                        title="View Details"
                      >
                        <ViewIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(debtor)}
                        title="Edit"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() =>
                          router.push(`/finance/debtors/${debtor.id}/receipts`)
                        }
                        title="Receipts"
                      >
                        <ReceiptIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteDebtor(debtor.id)}
                        title="Delete"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Add/Edit Dialog */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {selectedDebtor ? "Edit Debtor" : "Add New Debtor"}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={formData.fullName}
                  onChange={(e) =>
                    setFormData({ ...formData, fullName: e.target.value })
                  }
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Secondary Email"
                  type="email"
                  value={formData.secondaryEmail}
                  onChange={(e) =>
                    setFormData({ ...formData, secondaryEmail: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Tertiary Email"
                  type="email"
                  value={formData.tertiaryEmail}
                  onChange={(e) =>
                    setFormData({ ...formData, tertiaryEmail: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Contact Phone"
                  value={formData.contactPhone}
                  onChange={(e) =>
                    setFormData({ ...formData, contactPhone: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Development</InputLabel>
                  <Select
                    value={formData.developmentId}
                    label="Development"
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        developmentId: e.target.value,
                      })
                    }
                  >
                    {developments.map((dev) => (
                      <MenuItem key={dev.id} value={dev.id}>
                        {dev.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Unit Reference"
                  value={formData.unitRef}
                  onChange={(e) =>
                    setFormData({ ...formData, unitRef: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Property Type</InputLabel>
                  <Select
                    value={formData.propertyType}
                    label="Property Type"
                    onChange={(e) =>
                      setFormData({ ...formData, propertyType: e.target.value })
                    }
                  >
                    <MenuItem value="Residential">Residential</MenuItem>
                    <MenuItem value="Commercial">Commercial</MenuItem>
                    <MenuItem value="Industrial">Industrial</MenuItem>
                    <MenuItem value="Land">Land</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Purchase Price"
                  type="number"
                  value={formData.purchasePrice}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      purchasePrice: Number(e.target.value),
                    })
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Deposit Paid"
                  type="number"
                  value={formData.depositPaid}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      depositPaid: Number(e.target.value),
                    })
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Interest Rate (%)"
                  type="number"
                  value={formData.interestRate}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      interestRate: Number(e.target.value),
                    })
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Sales Rep"
                  value={formData.salesRep}
                  onChange={(e) =>
                    setFormData({ ...formData, salesRep: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={3}
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData({ ...formData, notes: e.target.value })
                  }
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveDebtor}>
              {selectedDebtor ? "Update" : "Create"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Import Dialog */}
        <ImportDialog
          open={importDialogOpen}
          onClose={() => setImportDialogOpen(false)}
          type="debtors"
          onImportComplete={fetchDebtors}
        />
      </Box>
    </FinanceContainer>
  );
};

export default DebtorsList;
